export const formatCurrency=(amount: number,currency: 'AUD' | 'USD'='AUD'): string=> {
  return new Intl.NumberFormat('en-AU',{style: 'currency',currency: currency,minimumFractionDigits: 2,maximumFractionDigits: 2}).format(amount);
};
export const formatDate=(dateString: string): string=> {
  try {
    const date=new Date(dateString);
    return new Intl.DateTimeFormat('en-AU',{year: 'numeric',month: 'short',day: 'numeric'}).format(date);
  } catch {
    return dateString;
  }
};
export const formatStatus=(status: string): string=> {
  return status.split('_').map(word=> word.charAt(0).toUpperCase() + word.slice(1) ).join(' ');
};
export const formatInvoiceNumber=(prefix: string,nextNumber: number): string=> {
  return `${prefix}-${nextNumber.toString().padStart(4,'0')}`;
};
export const formatPaymentType=(type: string): string=> {
  return type.split('_').map(word=> word.charAt(0).toUpperCase() + word.slice(1) ).join(' ');
};
export const formatPaymentMethod=(method: string): string=> {
  return method.replace('_',' ').split(' ').map(word=> word.charAt(0).toUpperCase() + word.slice(1) ).join(' ');
};
export const formatFrequency=(frequency: string): string=> {
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
};
export const formatReportDate=(dateString: string): string=> {
  try {
    const date=new Date(dateString);
    return new Intl.DateTimeFormat('en-AU',{year: 'numeric',month: 'short',day: 'numeric',hour: '2-digit',minute: '2-digit'}).format(date);
  } catch {
    return dateString;
  }
};
export type NormalisedPhoneNumber={
  value: string;
  isValid: boolean;
  error?: string;
};
const formatAustralianLocalPhone=(digits: string): string | null=> {
  if(/^04\d{8}$/.test(digits)){
    return `${digits.slice(0,4)} ${digits.slice(4,7)} ${digits.slice(7)}`;
  }
  if(/^0[2378]\d{8}$/.test(digits)){
    return `${digits.slice(0,2)} ${digits.slice(2,6)} ${digits.slice(6)}`;
  }
  if(/^13\d{4}$/.test(digits)){
    return `${digits.slice(0,2)} ${digits.slice(2,4)} ${digits.slice(4)}`;
  }
  if(/^(1300|1800)\d{6}$/.test(digits)){
    return `${digits.slice(0,4)} ${digits.slice(4,7)} ${digits.slice(7)}`;
  }
  return null;
};
const formatInternationalPhone=(digits: string): string=> {
  const groups: string[]=[];
  let remaining=digits;
  while(remaining.length>4){
    groups.unshift(remaining.slice(-3));
    remaining=remaining.slice(0,-3);
  }
  groups.unshift(remaining);
  return `+${groups.join(' ')}`;
};
export const normalisePhoneNumber=(rawValue: any): NormalisedPhoneNumber=> {
  const raw=String(rawValue??'').trim();
  if(!raw){
    return {
      value: '',
      isValid: true
    };
  }
  if(/[eE][+-]?\d+/.test(raw) || /^\d+\.\d+$/.test(raw)){
    return {
      value: raw,
      isValid: false,
      error: 'Phone number is stored in spreadsheet scientific or decimal format. Format the source phone column as Text and restore the complete digits before importing.'
    };
  }
  if(/[a-z]/i.test(raw)){
    return {
      value: raw,
      isValid: false,
      error: 'Phone number contains letters. Enter the complete number using digits,spaces,brackets,hyphens,or a leading plus sign only.'
    };
  }
  let compact=raw.replace(/\u00a0/g,' ').replace(/[\s().-]/g,'');
  if(compact.startsWith('0011')){
    compact=`+${compact.slice(4)}`;
  }else if(compact.startsWith('00')){
    compact=`+${compact.slice(2)}`;
  }
  if(!/^\+?\d+$/.test(compact)){
    return {
      value: raw,
      isValid: false,
      error: 'Phone number contains unsupported characters.'
    };
  }
  let digits=compact.replace(/\D/g,'');
  if(digits.startsWith('610') && digits.length===12){
    digits=`61${digits.slice(3)}`;
  }
  let localDigits=digits;
  if(digits.startsWith('61') && digits.length===11){
    localDigits=`0${digits.slice(2)}`;
  }else if(digits.length===9 && /^[23478]/.test(digits)){
    localDigits=`0${digits}`;
  }
  const australianPhone=formatAustralianLocalPhone(localDigits);
  if(australianPhone){
    return {
      value: australianPhone,
      isValid: true
    };
  }
  if(/^[1-9]\d{7,14}$/.test(digits)){
    return {
      value: formatInternationalPhone(digits),
      isValid: true
    };
  }
  return {
    value: raw,
    isValid: false,
    error: 'Phone number is not a recognised Australian number or a valid international number.'
  };
};
export const formatPhoneNumber=(value: any): string=> {
  const result=normalisePhoneNumber(value);
  return result.isValid ? result.value : String(value??'').trim();
};
export const formatPhoneNumberForCsv=(value: any): string=> formatPhoneNumber(value);
export const phoneDigitsForComparison=(value: any): string=> {
  const result=normalisePhoneNumber(value);
  return (result.isValid ? result.value : String(value??'')).replace(/\D/g,'');
};